import React from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddOutlined, CloseOutlined,
  ShortTextOutlined, RadioButtonCheckedOutlined, CheckBoxOutlined,
  ViewQuiltOutlined, SettingsOutlined, GridViewOutlined, ViewColumnOutlined,
  ImageOutlined, TextFieldsOutlined,
  VerticalAlignTopOutlined, VerticalAlignCenterOutlined, VerticalAlignBottomOutlined,
  PublicOutlined, CheckCircleOutlined, AutoAwesomeOutlined,
  HowToRegOutlined, LockOutlined, BadgeOutlined,
} from '@mui/icons-material';
import {
  InspectorSection, StyleRow, StyleInput, StyleSelect, ColorInput, PanelHeader,
} from '../eventPagesTab/ui/primitives';
import { CommitInput, ToggleRow } from '../eventPagesTab/inspectors';
import {
  FIELD_COLOR, SUCCESS_COLOR, SELECTION_COLOR,
  FIELD_TYPES, FIELD_GROUPS, LAYOUTS,
} from './constants';
import { slugify, uid } from './helpers';
import { MediaField } from '../lobbyBuilderTab/MediaField';
import { GradientBuilder } from '../eventPagesTab/ui/GradientBuilder';
import { FORM_TEMPLATES } from './formTemplates';

// ─────────────────────────────────────────────────────────────────────────────
// Shared: inline checkbox
// ─────────────────────────────────────────────────────────────────────────────

export function InlineCheck({ checked, onChange, label, color }) {
  return (
    <Box onClick={() => onChange(!checked)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
      <Box sx={{ width: 16, height: 16, borderRadius: '4px', border: `2px solid ${checked ? color : '#c0c8d4'}`, background: checked ? color : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.12s' }}>
        {checked && <svg viewBox="0 0 8 8" style={{ width: 9, height: 9 }}><polyline points="1,4 3,6 7,2" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </Box>
      {label && <Typography sx={{ fontSize: '0.78rem', color: checked ? '#1a1a2e' : '#9ea8b3' }}>{label}</Typography>}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Panel tab bar
// ─────────────────────────────────────────────────────────────────────────────

export function PanelTabs({ tabs, active, onChange, accentColor }) {
  const col = accentColor || FIELD_COLOR;
  return (
    <Box sx={{ display: 'flex', borderBottom: '1px solid #e8edf2', flexShrink: 0 }}>
      {tabs.map(t => (
        <Box key={t.key} onClick={() => onChange(t.key)}
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, py: 1.25, cursor: 'pointer', fontSize: '0.71rem', fontWeight: active === t.key ? 700 : 500, color: active === t.key ? col : '#9ea8b3', borderBottom: active === t.key ? `2px solid ${col}` : '2px solid transparent', transition: 'all 0.12s', userSelect: 'none' }}>
          {t.icon}{t.label}
        </Box>
      ))}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout grid picker (used in FormSettingsPanel)
// ─────────────────────────────────────────────────────────────────────────────

export function LayoutGrid({ value, onChange, accentColor }) {
  const col = accentColor || FIELD_COLOR;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px' }}>
      {LAYOUTS.map(l => {
        const active = value === l.value;
        return (
          <Box key={l.value} onClick={() => onChange(l.value)}
            sx={{ borderRadius: '8px', border: `1.5px solid ${active ? col : '#e4e9f0'}`, background: active ? alpha(col, 0.05) : '#fff', p: '8px 4px 6px', cursor: 'pointer', transition: 'all 0.12s', boxShadow: active ? `0 0 0 2px ${alpha(col, 0.2)}` : 'none', '&:hover': { borderColor: active ? col : alpha(col, 0.4) }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <Box sx={{ color: active ? col : '#9ea8b3' }}>{l.icon}</Box>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: active ? 700 : 500, color: active ? col : '#7a8494', textAlign: 'center', lineHeight: 1.2 }}>{l.label}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Field palette (drag source)
// ─────────────────────────────────────────────────────────────────────────────

// Singleton field types — only one of each is allowed per form
const SINGLETON_TYPES = new Set(['firstName', 'lastName', 'email', 'phone']);

export function FieldPalette({ fields = [] }) {
  // Collect all field types currently used (including inside column groups)
  const usedSingletons = new Set(
    (fields || [])
      .flatMap(f => {
        if (f.type === 'column_group') {
          return (f.columns || []).flatMap(c => (c.fields || []).map(cf => cf.type));
        }
        return [f.type];
      })
      .filter(t => SINGLETON_TYPES.has(t))
  );

  return (
    <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.25 }}>Fields</Typography>
      {FIELD_GROUPS.map(group => (
        <Box key={group} sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#b0bac8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>{group}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {Object.entries(FIELD_TYPES).filter(([, d]) => d.group === group).map(([type, def]) => {
              const isSingleton = SINGLETON_TYPES.has(type);
              const isUsed      = isSingleton && usedSingletons.has(type);
              return (
                <Box key={type}
                  draggable={!isUsed}
                  onDragStart={e => { if (isUsed) { e.preventDefault(); return; } e.dataTransfer.setData('fieldType', type); }}
                  title={isUsed ? 'Already added to this form' : undefined}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.25, py: 0.75, borderRadius: 1.5,
                    border: `1px solid ${isUsed ? '#f0f2f5' : '#e8edf2'}`,
                    background: isUsed ? '#f8f9fb' : '#fff',
                    cursor: isUsed ? 'not-allowed' : 'grab',
                    fontSize: '0.78rem',
                    color: isUsed ? '#c0c8d4' : '#3c4555',
                    transition: 'all 0.1s',
                    userSelect: 'none',
                    ...(!isUsed && {
                      '&:hover': { background: alpha(FIELD_COLOR, 0.05), borderColor: alpha(FIELD_COLOR, 0.25), color: FIELD_COLOR },
                      '&:active': { cursor: 'grabbing' },
                    }),
                  }}>
                  <Box sx={{ color: 'inherit', display: 'flex', flexShrink: 0 }}>{def.icon}</Box>
                  <Box sx={{ flex: 1 }}>{def.label}</Box>
                  {isUsed && (
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#c0c8d4', letterSpacing: '0.02em', flexShrink: 0 }}>
                      Already added
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Selection palette (shown when selection step is active, no group selected)
// ─────────────────────────────────────────────────────────────────────────────

export function SelectionPalette({ onAddGroup }) {
  return (
    <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
      <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, background: alpha(SELECTION_COLOR, 0.06), border: `1px solid ${alpha(SELECTION_COLOR, 0.2)}` }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: SELECTION_COLOR, mb: 0.35 }}>Selection Step</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#7a8494', lineHeight: 1.5 }}>
          Selection steps let users pick from visual cards — ideal for ticket types, session tracks, or preferences.
        </Typography>
      </Box>
      <Box onClick={onAddGroup}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.9, borderRadius: 1.5, border: `1.5px dashed ${alpha(SELECTION_COLOR, 0.4)}`, color: SELECTION_COLOR, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', '&:hover': { background: alpha(SELECTION_COLOR, 0.05), borderColor: SELECTION_COLOR } }}>
        <AddOutlined sx={{ fontSize: 15 }} /> Add selection group
      </Box>
      <Typography sx={{ mt: 2, fontSize: '0.65rem', color: '#b0bac8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, mb: 0.75 }}>Group types</Typography>
      {[
        { label: 'Single select', sub: 'User picks exactly one item', icon: <RadioButtonCheckedOutlined sx={{ fontSize: 15 }} /> },
        { label: 'Multi select',  sub: 'User picks one or more',      icon: <CheckBoxOutlined sx={{ fontSize: 15 }} /> },
      ].map(t => (
        <Box key={t.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.25, py: 0.85, borderRadius: 1.5, border: '1px solid #f0f2f5', background: '#fafbfc', mb: '4px' }}>
          <Box sx={{ color: '#c0c8d4', mt: '1px' }}>{t.icon}</Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#5a6474' }}>{t.label}</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8' }}>{t.sub}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SelectionGroupInspector
// ─────────────────────────────────────────────────────────────────────────────

export function SelectionGroupInspector({ group, onChange, onClose }) {
  const up = (k, v) => onChange({ ...group, [k]: v });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<GridViewOutlined sx={{ fontSize: 14 }} />} label="Selection Group" color={SELECTION_COLOR} onClose={onClose} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto' }}>
        <InspectorSection label="Group Settings">
          <StyleRow label="Title"><StyleInput value={group.title} onChange={v => up('title', v)} placeholder="Group title…" /></StyleRow>
          <StyleRow label="Subtitle"><StyleInput value={group.subtitle || ''} onChange={v => up('subtitle', v)} placeholder="Optional subtitle…" /></StyleRow>
          <StyleRow label="Badge"><StyleInput value={group.badge || ''} onChange={v => up('badge', v)} placeholder="01" /></StyleRow>
          <StyleRow label="Mode">
            <StyleSelect value={group.selectionMode || 'single'} onChange={v => up('selectionMode', v)}
              options={[{ value: 'single', label: 'Single select' }, { value: 'multi', label: 'Multi select' }]} />
          </StyleRow>
          <StyleRow label="Columns">
            <StyleSelect value={String(group.columns || 3)} onChange={v => up('columns', Number(v))}
              options={[{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }]} />
          </StyleRow>
          <StyleRow label="Required">
            <InlineCheck checked={!!group.required} onChange={v => up('required', v)} label={group.required ? 'Required' : 'Optional'} color={SELECTION_COLOR} />
          </StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Items">
          {(group.items || []).map((item, i) => (
            <Box key={item.id} sx={{ mb: 0.75, p: 1, borderRadius: 1.5, border: '1px solid #e8edf2', background: '#fafbfc' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', flex: 1 }}>Item {i + 1}</Typography>
                <IconButton size="small" onClick={() => up('items', group.items.filter((_, idx) => idx !== i))}
                  disabled={group.items.length <= 1} sx={{ color: '#dc2626', width: 18, height: 18, opacity: group.items.length <= 1 ? 0.3 : 0.7 }}>
                  <CloseOutlined sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
              <StyleRow label="Title">
                <StyleInput value={item.title} onChange={v => up('items', group.items.map((it, idx) => idx === i ? { ...it, title: v } : it))} placeholder="Item title" />
              </StyleRow>
              <StyleRow label="Desc">
                <StyleInput value={item.description || ''} onChange={v => up('items', group.items.map((it, idx) => idx === i ? { ...it, description: v } : it))} placeholder="Description or session count…" />
              </StyleRow>
              <StyleRow label="Price">
                <StyleInput value={item.price ?? ''} onChange={v => up('items', group.items.map((it, idx) => idx === i ? { ...it, price: v === '' ? null : Number(v) } : it))} placeholder="0 = Free" />
              </StyleRow>
              <StyleRow label="Label">
                <StyleInput value={item.priceLabel || ''} onChange={v => up('items', group.items.map((it, idx) => idx === i ? { ...it, priceLabel: v } : it))} placeholder="Free / ₱250 / etc." />
              </StyleRow>
            </Box>
          ))}
          <Box
            onClick={() => up('items', [...(group.items || []), { id: uid(), title: `Option ${(group.items || []).length + 1}`, description: '', icon: 'Stars', price: null, currency: 'PHP', priceLabel: null, available: true, order: (group.items || []).length }])}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: SELECTION_COLOR, fontSize: '0.75rem', fontWeight: 600, mt: 0.5, '&:hover': { opacity: 0.75 } }}>
            <AddOutlined sx={{ fontSize: 14 }} /> Add item
          </Box>
        </InspectorSection>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OptionsEditor (used inside FieldInspector)
// ─────────────────────────────────────────────────────────────────────────────

function OptionsEditor({ options, onChange }) {
  const add    = () => { const n = options.length + 1; onChange([...options, { label: `Option ${n}`, value: `option_${n}` }]); };
  const remove = (i) => onChange(options.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(options.map((o, idx) => idx === i ? { label: val, value: slugify(val) } : o));
  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.75 }}>
        {options.map((o, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <input value={o.label} onChange={e => update(i, e.target.value)}
              style={{ flex: 1, border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = FIELD_COLOR}
              onBlur={e => e.target.style.borderColor = '#e4e9f0'} />
            <IconButton size="small" onClick={() => remove(i)} disabled={options.length <= 1} sx={{ color: '#dc2626', width: 20, height: 20, opacity: options.length <= 1 ? 0.3 : 0.7 }}>
              <CloseOutlined sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Box onClick={add} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: FIELD_COLOR, fontSize: '0.75rem', fontWeight: 600, '&:hover': { opacity: 0.75 } }}>
        <AddOutlined sx={{ fontSize: 14 }} /> Add option
      </Box>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// SelectionGroupFieldInspector
// Inspector for a selection_group field — same settings as a selectionGroup
// but living inline inside a fields step.
// ─────────────────────────────────────────────────────────────────────────────

function SelectionGroupFieldInspector({ field, onChange }) {
  const up = (k, v) => onChange({ ...field, [k]: v });
  const items = field.items || [];

  return (
    <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', flex: 1 }}>
      <InspectorSection label="Group Settings">
        <StyleRow label="Label">
          <StyleInput value={field.label || ''} onChange={v => up('label', v)} placeholder="e.g. Select Ticket Type" />
        </StyleRow>
        <StyleRow label="Subtitle">
          <StyleInput value={field.subtitle || ''} onChange={v => up('subtitle', v)} placeholder="Optional subtitle…" />
        </StyleRow>
        <StyleRow label="Badge">
          <StyleInput value={field.badge || ''} onChange={v => up('badge', v)} placeholder="01" />
        </StyleRow>
        <StyleRow label="Mode">
          <StyleSelect
            value={field.selectionMode || 'single'}
            onChange={v => up('selectionMode', v)}
            options={[
              { value: 'single', label: 'Single select' },
              { value: 'multi',  label: 'Multi select'  },
            ]}
          />
        </StyleRow>
        <StyleRow label="Columns">
          <StyleSelect
            value={String(field.columns || 3)}
            onChange={v => up('columns', Number(v))}
            options={[
              { value: '1', label: '1' },
              { value: '2', label: '2' },
              { value: '3', label: '3' },
              { value: '4', label: '4' },
            ]}
          />
        </StyleRow>
        <StyleRow label="Required">
          <InlineCheck
            checked={!!field.required}
            onChange={v => up('required', v)}
            label={field.required ? 'Required' : 'Optional'}
            color={SELECTION_COLOR}
          />
        </StyleRow>
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      <InspectorSection label="Items">
        {items.map((item, i) => (
          <Box key={item.id || i} sx={{ mb: 0.75, p: 1, borderRadius: 1.5, border: '1px solid #e8edf2', background: '#fafbfc' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', flex: 1 }}>Item {i + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => up('items', items.filter((_, idx) => idx !== i))}
                disabled={items.length <= 1}
                sx={{ color: '#dc2626', width: 18, height: 18, opacity: items.length <= 1 ? 0.3 : 0.7 }}
              >
                <CloseOutlined sx={{ fontSize: 11 }} />
              </IconButton>
            </Box>
            <StyleRow label="Title">
              <StyleInput
                value={item.title || ''}
                onChange={v => up('items', items.map((it, idx) => idx === i ? { ...it, title: v } : it))}
                placeholder="Item title"
              />
            </StyleRow>
            <StyleRow label="Desc">
              <StyleInput
                value={item.description || ''}
                onChange={v => up('items', items.map((it, idx) => idx === i ? { ...it, description: v } : it))}
                placeholder="Subtitle or session count…"
              />
            </StyleRow>
            <StyleRow label="Price">
              <StyleInput
                value={item.price ?? ''}
                onChange={v => up('items', items.map((it, idx) => idx === i ? { ...it, price: v === '' ? null : Number(v) } : it))}
                placeholder="0 = Free, blank = hidden"
              />
            </StyleRow>
            <StyleRow label="Label">
              <StyleInput
                value={item.priceLabel || ''}
                onChange={v => up('items', items.map((it, idx) => idx === i ? { ...it, priceLabel: v } : it))}
                placeholder="Free / ₱250 / Included"
              />
            </StyleRow>
          </Box>
        ))}
        <Box
          onClick={() => up('items', [
            ...items,
            { id: uid(), title: `Option ${items.length + 1}`, description: '', icon: null, price: null, currency: 'PHP', priceLabel: null, available: true, order: items.length },
          ])}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: SELECTION_COLOR, fontSize: '0.75rem', fontWeight: 600, mt: 0.5, '&:hover': { opacity: 0.75 } }}
        >
          <AddOutlined sx={{ fontSize: 14 }} /> Add item
        </Box>
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      <InspectorSection label="Helper Text">
        <StyleInput value={field.helperText || ''} onChange={v => up('helperText', v)} placeholder="Hint shown below the group…" />
      </InspectorSection>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ColumnGroupInspector
// Inspector for a column_group field — controls number of columns.
// Individual column children are edited by clicking fields inside them.
// ─────────────────────────────────────────────────────────────────────────────

function ColumnGroupInspector({ field, onChange }) {
  const up = (k, v) => onChange({ ...field, [k]: v });
  const cols = field.columns || [];

  const addColumn = () => {
    if (cols.length >= 4) return;
    up('columns', [...cols, { id: uid(), fields: [] }]);
  };

  const removeColumn = (colId) => {
    if (cols.length <= 1) return;
    up('columns', cols.filter(c => c.id !== colId));
  };

  return (
    <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', flex: 1 }}>
      <InspectorSection label="Layout">
        <Box sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', mb: 1 }}>
            Columns ({cols.length})
          </Typography>

          {/* Visual column count picker */}
          <Box sx={{ display: 'flex', gap: '6px', mb: 1.5 }}>
            {[1, 2, 3, 4].map(n => {
              const active = cols.length === n;
              return (
                <Box key={n} onClick={() => {
                  if (n === cols.length) return;
                  if (n > cols.length) {
                    // Add columns
                    const toAdd = Array.from({ length: n - cols.length }, () => ({ id: uid(), fields: [] }));
                    up('columns', [...cols, ...toAdd]);
                  } else {
                    // Remove from end — merge orphaned fields into last kept column
                    const kept    = cols.slice(0, n);
                    const removed = cols.slice(n);
                    const orphans = removed.flatMap(c => c.fields || []);
                    if (orphans.length > 0) {
                      kept[kept.length - 1] = {
                        ...kept[kept.length - 1],
                        fields: [...(kept[kept.length - 1].fields || []), ...orphans],
                      };
                    }
                    up('columns', kept);
                  }
                }}
                  sx={{
                    flex: 1, py: 1.25, borderRadius: '8px', cursor: 'pointer',
                    border: `1.5px solid ${active ? FIELD_COLOR : '#e4e9f0'}`,
                    background: active ? alpha(FIELD_COLOR, 0.06) : '#fff',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                    transition: 'all 0.12s',
                    boxShadow: active ? `0 0 0 2px ${alpha(FIELD_COLOR, 0.15)}` : 'none',
                    '&:hover': { borderColor: active ? FIELD_COLOR : alpha(FIELD_COLOR, 0.4) },
                  }}
                >
                  {/* Mini column diagram */}
                  <Box sx={{ display: 'flex', gap: '2px', width: '80%' }}>
                    {Array.from({ length: n }).map((_, i) => (
                      <Box key={i} sx={{ flex: 1, height: 18, borderRadius: '3px', background: active ? alpha(FIELD_COLOR, 0.25) : '#e8edf2' }} />
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: active ? 700 : 500, color: active ? FIELD_COLOR : '#9ea8b3' }}>
                    {n} col{n !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontSize: '0.67rem', color: '#b0bac8', lineHeight: 1.5 }}>
            Drag any field type into a column. Fields inside columns support all the same types, including nested columns.
          </Typography>
        </Box>
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      <InspectorSection label="Columns">
        {cols.map((col, i) => (
          <Box key={col.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, px: 1, py: 0.75, borderRadius: '8px', border: '1px solid #f0f2f5', background: '#fafbfc' }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '5px', background: alpha(FIELD_COLOR, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: FIELD_COLOR }}>{i + 1}</Typography>
            </Box>
            <Typography sx={{ flex: 1, fontSize: '0.75rem', color: '#5a6474' }}>
              Column {i + 1}
              <Box component="span" sx={{ ml: 0.75, fontSize: '0.65rem', color: '#b0bac8' }}>
                {(col.fields || []).length} field{(col.fields || []).length !== 1 ? 's' : ''}
              </Box>
            </Typography>
            <IconButton size="small" onClick={() => removeColumn(col.id)}
              disabled={cols.length <= 1}
              sx={{ width: 18, height: 18, color: '#dc2626', opacity: cols.length <= 1 ? 0.2 : 0.6, '&:hover': { opacity: 1, background: alpha('#dc2626', 0.08) } }}>
              <CloseOutlined sx={{ fontSize: 11 }} />
            </IconButton>
          </Box>
        ))}
        {cols.length < 4 && (
          <Box onClick={addColumn}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: FIELD_COLOR, fontSize: '0.75rem', fontWeight: 600, mt: 0.25, '&:hover': { opacity: 0.75 } }}>
            <AddOutlined sx={{ fontSize: 14 }} /> Add column
          </Box>
        )}
      </InspectorSection>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldInspector
// ─────────────────────────────────────────────────────────────────────────────

export function FieldInspector({ field, onChange, onClose }) {
  const up   = (k, v) => onChange({ ...field, [k]: v });
  const type = field._type || field.type;

  // Route special field types to their own inspectors
  if (type === 'selection_group') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader icon={FIELD_TYPES.selection_group.icon} label="Selection Group" color={SELECTION_COLOR} onClose={onClose} />
        <SelectionGroupFieldInspector field={field} onChange={onChange} />
      </Box>
    );
  }
  if (type === 'column_group') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader icon={FIELD_TYPES.column_group.icon} label="Columns" color={FIELD_COLOR} onClose={onClose} />
        <ColumnGroupInspector field={field} onChange={onChange} />
      </Box>
    );
  }

  const hasOptions  = ['select', 'radio', 'checkbox'].includes(type);
  const hasRequired = !['heading', 'divider'].includes(type);
  const isLayout    = ['heading', 'divider'].includes(type);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={FIELD_TYPES[type]?.icon || <ShortTextOutlined sx={{ fontSize: 14 }} />} label={FIELD_TYPES[type]?.label || type} color={FIELD_COLOR} onClose={onClose} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto' }}>
        <InspectorSection label="Content">
          {type === 'heading' ? (
            <>
              <StyleRow label="Text"><StyleInput value={field.content} onChange={v => up('content', v)} placeholder="Heading text…" /></StyleRow>
              <StyleRow label="Level"><StyleSelect value={field.level || 'h3'} onChange={v => up('level', v)} options={[{ value: 'h1', label: 'H1' }, { value: 'h2', label: 'H2' }, { value: 'h3', label: 'H3' }, { value: 'h4', label: 'H4' }]} /></StyleRow>
            </>
          ) : type !== 'divider' ? (
            <>
              <StyleRow label="Label"><StyleInput value={field.label} onChange={v => up('label', v)} placeholder="Field label" /></StyleRow>
              {hasRequired && <StyleRow label="Required"><InlineCheck checked={!!field.required} onChange={v => up('required', v)} label={field.required ? 'Required' : 'Optional'} color={FIELD_COLOR} /></StyleRow>}
              {['text', 'email', 'phone', 'number', 'textarea', 'firstName', 'lastName'].includes(type) && <StyleRow label="Placeholder"><StyleInput value={field.placeholder || ''} onChange={v => up('placeholder', v)} placeholder="Placeholder…" /></StyleRow>}
              {type === 'textarea' && <StyleRow label="Rows"><input type="number" min={2} max={20} value={field.rows || 4} onChange={e => up('rows', parseInt(e.target.value) || 4)} style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb' }} /></StyleRow>}
              {type === 'number'   && <><StyleRow label="Min"><StyleInput value={field.min ?? ''} onChange={v => up('min', v === '' ? '' : Number(v))} placeholder="—" /></StyleRow><StyleRow label="Max"><StyleInput value={field.max ?? ''} onChange={v => up('max', v === '' ? '' : Number(v))} placeholder="—" /></StyleRow></>}
              {type === 'file'     && <><StyleRow label="Accept"><StyleInput value={field.accept || ''} onChange={v => up('accept', v)} placeholder=".pdf,.jpg" /></StyleRow><StyleRow label="Max MB"><StyleInput value={field.maxSizeMb || ''} onChange={v => up('maxSizeMb', Number(v))} placeholder="5" /></StyleRow></>}
              {type === 'rating'   && <StyleRow label="Stars"><input type="number" min={3} max={10} value={field.max || 5} onChange={e => up('max', parseInt(e.target.value) || 5)} style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb' }} /></StyleRow>}
              {type === 'scale'    && <><StyleRow label="Min"><StyleInput value={field.min ?? 1} onChange={v => up('min', Number(v))} /></StyleRow><StyleRow label="Max"><StyleInput value={field.max ?? 10} onChange={v => up('max', Number(v))} /></StyleRow></>}
              {type === 'toggle'   && <StyleRow label="Default"><StyleSelect value={field.defaultValue ? 'true' : 'false'} onChange={v => up('defaultValue', v === 'true')} options={[{ value: 'false', label: 'Off' }, { value: 'true', label: 'On' }]} /></StyleRow>}
            </>
          ) : <Typography sx={{ fontSize: '0.75rem', color: '#9ea8b3' }}>No settings for this element.</Typography>}
        </InspectorSection>
        {hasOptions && (<><Divider sx={{ borderColor: '#f0f2f5' }} /><InspectorSection label="Options"><OptionsEditor options={field.options || []} onChange={v => up('options', v)} /></InspectorSection></>)}
        {!isLayout  && (<><Divider sx={{ borderColor: '#f0f2f5' }} /><InspectorSection label="Helper Text"><StyleInput value={field.helperText || ''} onChange={v => up('helperText', v)} placeholder="Hint shown below the field…" /></InspectorSection></>)}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormSettingsPanel
// ─────────────────────────────────────────────────────────────────────────────

export function FormSettingsPanel({ settings, onChange, activeForm, event, onFormMetaChange, onSlugAvailabilityChange, loginMethodMismatch, onResyncFields }) {
  const br  = settings.branding || {};
  const upB = (k, v) => onChange({ ...settings, branding: { ...br, [k]: v } });

  // ── Slug availability check ───────────────────────────────────────────────
  const [slugAvailable, setSlugAvailable] = React.useState(null);
  const [checkingSlug, setCheckingSlug]   = React.useState(false);
  const debounceRef = React.useRef(null);

  React.useEffect(() => { setSlugAvailable(null); }, [activeForm?._id]);

  React.useEffect(() => {
    onSlugAvailabilityChange?.(slugAvailable);
  }, [slugAvailable]); // eslint-disable-line 

  const checkSlug = React.useCallback((slug) => {
    if (!slug) return;
    clearTimeout(debounceRef.current);
    setCheckingSlug(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { api } = await import('../../../hooks/useApi');
        const res = await api.get('/forms/check-slug', {
          params: { slug, excludeId: activeForm?._id },
        });
        setSlugAvailable(res.data?.available ?? true);
      } catch {
        setSlugAvailable(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 500);
  }, [activeForm?._id]);

  const slugify = (s) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');

  const loginMethod   = event?.login?.method || 'email';
  const isRegistration = activeForm?.isRegistration || false;
  const empLabel       = activeForm?.employeeFieldLabel || 'Employee Number';

  const METHOD_INFO = {
    email:          { icon: <BadgeOutlined sx={{ fontSize: 14 }} />, label: 'Email + Password', fields: ['Email Address (required)', 'Password (required)'] },
    employeeNumber: { icon: <BadgeOutlined sx={{ fontSize: 14 }} />, label: `${empLabel} field`, fields: [`${empLabel} (required, locked)`] },
    magicLink:      { icon: <BadgeOutlined sx={{ fontSize: 14 }} />, label: 'Magic Link', fields: ['Email Address (required, locked)'] },
  };
  const methodInfo = METHOD_INFO[loginMethod] || METHOD_INFO.email;

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Identity ── */}
      <InspectorSection label="Identity">
        <StyleRow label="Name">
          <CommitInput
            value={activeForm?.name || ''}
            onChange={v => onFormMetaChange?.({ name: v })}
            placeholder="e.g. Registration Form"
          />
        </StyleRow>
        <StyleRow label="Slug">
          <CommitInput
            value={activeForm?.slug || ''}
            onChange={v => {
              const clean = slugify(v);
              onFormMetaChange?.({ slug: clean });
              setSlugAvailable(null);
              checkSlug(clean);
            }}
            placeholder="e.g. registration-form"
            prefix="/"
            monospace
          />
          {checkingSlug && (
            <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mt: 0.5, pl: 0.25 }}>
              Checking availability…
            </Typography>
          )}
          {!checkingSlug && slugAvailable === false && (
            <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', mt: 0.5, pl: 0.25 }}>
              Slug name not available. Please use another.
            </Typography>
          )}
          {!checkingSlug && slugAvailable === true && (
            <Typography sx={{ fontSize: '0.65rem', color: '#22c55e', mt: 0.5, pl: 0.25 }}>
              Slug is available ✓
            </Typography>
          )}
        </StyleRow>
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      {/* ── Registration ── */}
      <InspectorSection label="Registration">
        <ToggleRow
          label="Registration Form"
          description="Make this form accept participants"
          checked={isRegistration}
          onChange={v => onFormMetaChange?.({ isRegistration: v })}
          icon={<HowToRegOutlined sx={{ fontSize: 15 }} />}
          accentColor={FIELD_COLOR}
        />

        {/* ── Login method mismatch warning ── */}
        {/* {isRegistration && loginMethodMismatch && (
          <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, background: alpha('#f59e0b', 0.07), border: '1px solid #fde68a', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
              <Box sx={{ mt: '1px', flexShrink: 0, width: 14, height: 14, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>!</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.65rem', color: '#92400e', lineHeight: 1.5, fontWeight: 600 }}>
                The event login type has changed.
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.65rem', color: '#78350f', lineHeight: 1.5 }}>
              Some auth fields no longer match the current login method.
            </Typography>
            <Box
              onClick={onResyncFields}
              sx={{ mt: 0.25, px: 1.25, py: 0.65, borderRadius: 1.25, background: '#f59e0b', color: '#fff', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', textAlign: 'center', transition: 'opacity 0.15s', '&:hover': { opacity: 0.88 }, userSelect: 'none' }}
            >
              Remove unused fields &amp; add correct ones
            </Box>
          </Box>
        )} */}

        {isRegistration && (
          <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, background: alpha(FIELD_COLOR, 0.05), border: `1px solid ${alpha(FIELD_COLOR, 0.15)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <LockOutlined sx={{ fontSize: 12, color: FIELD_COLOR }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: FIELD_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {methodInfo.label}
              </Typography>
            </Box>
            {methodInfo.fields.map((f, i) => (
              <Typography key={i} sx={{ fontSize: '0.65rem', color: '#5a6474', mb: 0.4, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', background: FIELD_COLOR, display: 'inline-block', flexShrink: 0 }} />
                {f}
              </Typography>
            ))}

            {loginMethod === 'employeeNumber' && (
              <Box sx={{ mt: 1.25 }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#5a6474', mb: 0.5 }}>Field label</Typography>
                <StyleInput
                  value={empLabel}
                  onChange={v => onFormMetaChange?.({ employeeFieldLabel: v || 'Employee Number' })}
                  placeholder="e.g. PRC Number, Staff ID…"
                />
              </Box>
            )}
          </Box>
        )}
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      {/* ── Visibility ── */}
      <InspectorSection label="Visibility">
        <ToggleRow
          label="Published"
          description="Visible to public visitors"
          checked={activeForm?.status === 'published'}
          onChange={v => onFormMetaChange?.({ status: v ? 'published' : 'draft' })}
          icon={<PublicOutlined sx={{ fontSize: 15 }} />}
          accentColor="#22c55e"
        />
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      {/* ── Layout ── */}
      <InspectorSection label="Layout">
        <LayoutGrid value={br.layout || 'card-left'} onChange={v => upB('layout', v)} accentColor={FIELD_COLOR} />
      </InspectorSection>

    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SuccessEditor
// ─────────────────────────────────────────────────────────────────────────────

export function SuccessEditor({ successPage, onChange }) {
  const sp = successPage || {};
  const up = (k, v) => onChange({ ...sp, [k]: v });
  const upCta = (cta, k, v) => onChange({ ...sp, [cta]: { ...(sp[cta] || {}), [k]: v } });
  return (
    <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', flex: 1 }}>
      <InspectorSection label="Copy">
        <StyleRow label="Subtitle"><StyleInput value={sp.subtitle || ''} onChange={v => up('subtitle', v)} placeholder="Registration Complete" /></StyleRow>
        <StyleRow label="Title"><StyleInput value={sp.title || ''} onChange={v => up('title', v)} placeholder="You're registered!" /></StyleRow>
        <Box sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#7a8494', mb: 0.5 }}>Message</Typography>
          <textarea value={sp.message || ''} onChange={e => up('message', e.target.value)} rows={3} placeholder="Thank you for signing up…"
            style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '6px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', resize: 'vertical', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = SUCCESS_COLOR}
            onBlur={e => e.target.style.borderColor = '#e4e9f0'} />
        </Box>
        <StyleRow label="Support"><StyleInput value={sp.supportText || ''} onChange={v => up('supportText', v)} placeholder="Questions? Contact…" /></StyleRow>
      </InspectorSection>
      <Divider sx={{ borderColor: '#f0f2f5' }} />
      <InspectorSection label="Primary CTA">
        <StyleRow label="Label"><StyleInput value={sp.ctaPrimary?.label || ''} onChange={v => upCta('ctaPrimary', 'label', v)} placeholder="View Agenda" /></StyleRow>
        <StyleRow label="URL"><StyleInput value={sp.ctaPrimary?.url || ''} onChange={v => upCta('ctaPrimary', 'url', v)} placeholder="https://…" /></StyleRow>
      </InspectorSection>
      <Divider sx={{ borderColor: '#f0f2f5' }} />
      <InspectorSection label="Secondary CTA">
        <StyleRow label="Label"><StyleInput value={sp.ctaSecondary?.label || ''} onChange={v => upCta('ctaSecondary', 'label', v)} placeholder="Back to Home" /></StyleRow>
        <StyleRow label="URL"><StyleInput value={sp.ctaSecondary?.url || ''} onChange={v => upCta('ctaSecondary', 'url', v)} placeholder="https://…" /></StyleRow>
      </InspectorSection>
      <Divider sx={{ borderColor: '#f0f2f5' }} />
      <InspectorSection label="Options">
        <InlineCheck checked={sp.showEventDetails ?? true} onChange={v => up('showEventDetails', v)} label="Show event details cards" color={SUCCESS_COLOR} />
      </InspectorSection>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeroBannerInspector — Solid / Gradient / Media (image+video) + overlay + align
// ─────────────────────────────────────────────────────────────────────────────
export function HeroBannerInspector({ settings, onSettingsChange, onClose }) {
  const br       = settings.branding || {};
  const bannerBg = br.bannerBg || {};

  const updateBg = (key, val) =>
    onSettingsChange({ ...settings, branding: { ...br, bannerBg: { ...bannerBg, [key]: val } } });
  const updateBgMulti = (patch) =>
    onSettingsChange({ ...settings, branding: { ...br, bannerBg: { ...bannerBg, ...patch } } });
  const upB = (k, v) => onSettingsChange({ ...settings, branding: { ...br, [k]: v } });

  const bg = bannerBg.background || '';

  const detectBgType = () => {
    if (bannerBg.type === 'video' || bannerBg.type === 'image') return 'media';
    if (bg.includes('gradient')) return 'gradient';
    if (bg.startsWith('url(')) return 'media';
    if (bg && !bg.startsWith('url(')) return 'solid';
    return 'solid';
  };
  const [bgType, setBgType] = React.useState(detectBgType);
  React.useEffect(() => { setBgType(detectBgType()); }, [bannerBg.type, bg]);

  const [mediaSubtype, setMediaSubtype] = React.useState(
    bannerBg.type === 'video' ? 'video' : 'image'
  );
  React.useEffect(() => {
    setMediaSubtype(bannerBg.type === 'video' ? 'video' : 'image');
  }, [bannerBg.type]);

  const switchType = (type) => {
    setBgType(type);
    const base = { ...bannerBg };
    if (type === 'solid')    { base.type = 'solid';    base.background = bg && !bg.startsWith('url') && !bg.includes('gradient') ? bg : '#1565C0'; }
    if (type === 'gradient') { base.type = 'gradient'; base.background = bg.includes('gradient') ? bg : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; }
    if (type === 'media')    { base.type = mediaSubtype; if (mediaSubtype !== 'video') base.background = bg.startsWith('url(') ? bg : ''; }
    onSettingsChange({ ...settings, branding: { ...br, bannerBg: base } });
  };

  const switchMediaSubtype = (sub) => {
    setMediaSubtype(sub);
    updateBgMulti({ type: sub, ...(sub === 'video' ? { background: '' } : {}) });
  };

  const BgTypeBtn = ({ type, label }) => (
    <Box onClick={() => switchType(type)} sx={{
      flex: 1, py: 0.6, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600,
      borderRadius: 1, cursor: 'pointer', transition: 'all 0.1s',
      background: bgType === type ? FIELD_COLOR : 'transparent',
      color: bgType === type ? '#fff' : '#7a8494',
      '&:hover': { color: bgType === type ? '#fff' : FIELD_COLOR },
    }}>{label}</Box>
  );

  const bgImageUrl = bg.match(/url\(['"]?([^'"()]+)['"]?\)/)?.[1] || '';
  const hasMedia   = bgType === 'media' && (bgImageUrl || bannerBg.videoUrl);

  const ALIGN_OPTIONS = [
    { value: 'flex-start', icon: <VerticalAlignTopOutlined sx={{ fontSize: 16 }} />, label: 'Top' },
    { value: 'center',     icon: <VerticalAlignCenterOutlined sx={{ fontSize: 16 }} />, label: 'Center' },
    { value: 'flex-end',   icon: <VerticalAlignBottomOutlined sx={{ fontSize: 16 }} />, label: 'Bottom' },
  ];
  const contentAlign = bannerBg.contentAlign || 'center';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PanelHeader
        icon={<ImageOutlined sx={{ fontSize: 14, color: FIELD_COLOR }} />}
        label="Banner"
        onClose={onClose}
        color={FIELD_COLOR}
      />
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

        {/* ── Background ── */}
        <InspectorSection label="Background">
          <Box sx={{ display: 'flex', background: '#f0f4f8', borderRadius: 1.5, p: '3px', gap: '3px', mb: 1.5 }}>
            <BgTypeBtn type="solid"    label="Solid" />
            <BgTypeBtn type="gradient" label="Gradient" />
            <BgTypeBtn type="media"    label="Media" />
          </Box>

          {bgType === 'solid' && (
            <StyleRow label="Color">
              <ColorInput
                value={bg && !bg.startsWith('url') && !bg.includes('gradient') ? bg : '#1565C0'}
                onChange={v => updateBgMulti({ background: v, type: 'solid' })}
              />
            </StyleRow>
          )}

          {bgType === 'gradient' && (
            <GradientBuilder
              value={bg.includes('gradient') ? bg : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}
              onChange={v => updateBgMulti({ background: v, type: 'gradient' })}
            />
          )}

          {bgType === 'media' && (
            <>
              <StyleRow label="Type">
                <StyleSelect
                  value={mediaSubtype}
                  onChange={switchMediaSubtype}
                  options={[
                    { value: 'image', label: 'Image' },
                    { value: 'video', label: 'Video' },
                  ]}
                />
              </StyleRow>

              {mediaSubtype === 'image' && (
                <>
                  <StyleRow label="Image">
                    <MediaField
                      value={bgImageUrl}
                      onChange={url => updateBgMulti({
                        background: url ? `url('${url}') center/cover no-repeat` : '',
                        type: 'image',
                      })}
                      mediaType="image"
                    />
                  </StyleRow>
                  <StyleRow label="Size">
                    <StyleSelect
                      value={bannerBg.backgroundSize || 'cover'}
                      onChange={v => updateBg('backgroundSize', v)}
                      options={[
                        { value: 'cover',     label: 'Cover' },
                        { value: 'contain',   label: 'Contain' },
                        { value: '100% 100%', label: 'Stretch' },
                        { value: 'auto',      label: 'Auto' },
                      ]}
                    />
                  </StyleRow>
                  <StyleRow label="Position">
                    <StyleSelect
                      value={bannerBg.backgroundPosition || 'center'}
                      onChange={v => updateBg('backgroundPosition', v)}
                      options={[
                        { value: 'center',       label: 'Center' },
                        { value: 'top',          label: 'Top' },
                        { value: 'bottom',       label: 'Bottom' },
                        { value: 'left center',  label: 'Left' },
                        { value: 'right center', label: 'Right' },
                      ]}
                    />
                  </StyleRow>
                  {bgImageUrl && (
                    <Box sx={{ mt: 0.5, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${alpha(FIELD_COLOR, 0.2)}`, height: 80 }}>
                      <Box sx={{ width: '100%', height: '100%', background: `url('${bgImageUrl}') center/cover no-repeat` }} />
                    </Box>
                  )}
                </>
              )}

              {mediaSubtype === 'video' && (
                <StyleRow label="Video">
                  <MediaField
                    value={bannerBg.videoUrl || ''}
                    onChange={v => updateBg('videoUrl', v)}
                    mediaType="video"
                  />
                </StyleRow>
              )}
            </>
          )}
        </InspectorSection>

        {/* ── Content Align ── */}
        <Divider sx={{ borderColor: '#f0f2f5' }} />
        <InspectorSection label="Content Align">
          <Box sx={{ display: 'flex', gap: '4px' }}>
            {ALIGN_OPTIONS.map(opt => (
              <Box
                key={opt.value}
                onClick={() => updateBg('contentAlign', opt.value)}
                title={opt.label}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  py: 0.75, borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.12s',
                  border: `1.5px solid ${contentAlign === opt.value ? FIELD_COLOR : '#e4e9f0'}`,
                  background: contentAlign === opt.value ? alpha(FIELD_COLOR, 0.08) : '#f8f9fb',
                  color: contentAlign === opt.value ? FIELD_COLOR : '#9ea8b3',
                  '&:hover': { borderColor: FIELD_COLOR, color: FIELD_COLOR },
                }}
              >
                {opt.icon}
              </Box>
            ))}
          </Box>
        </InspectorSection>

        {/* ── Hero Text ── */}
        <Divider sx={{ borderColor: '#f0f2f5' }} />
        <InspectorSection label="Hero Text">
          <StyleRow label="Tagline">
            <StyleInput
              value={settings.headerTitle || ''}
              onChange={v => onSettingsChange({ ...settings, headerTitle: v })}
              placeholder="e.g. RESERVE YOUR SLOT"
            />
          </StyleRow>
          <StyleRow label="Title">
            <StyleInput
              value={settings.headerSubtitle || ''}
              onChange={v => onSettingsChange({ ...settings, headerSubtitle: v })}
              placeholder="e.g. Annual Conference 2025"
            />
          </StyleRow>
          <StyleRow label="Subtitle">
            <StyleInput
              value={settings.description || ''}
              onChange={v => onSettingsChange({ ...settings, description: v })}
              placeholder="Short description…"
            />
          </StyleRow>
          <StyleRow label="Text color">
            <ColorInput
              value={settings.heroTextColor || '#ffffff'}
              onChange={v => onSettingsChange({ ...settings, heroTextColor: v })}
            />
          </StyleRow>
        </InspectorSection>

        {/* ── Overlay — only for media ── */}
        {hasMedia && (
          <>
            <Divider sx={{ borderColor: '#f0f2f5' }} />
            <InspectorSection label="Overlay">
              <StyleRow label="Color">
                <ColorInput value={br.mediaOverlayColor || '#000000'} onChange={v => upB('mediaOverlayColor', v)} />
              </StyleRow>
              <StyleRow label="Opacity">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input type="range" min={0} max={1} step={0.05}
                    value={br.mediaOverlayOpacity ?? 0.45}
                    onChange={e => upB('mediaOverlayOpacity', parseFloat(e.target.value))}
                    style={{ flex: 1, accentColor: FIELD_COLOR, cursor: 'pointer' }} />
                  <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', width: 28, textAlign: 'right' }}>
                    {Math.round((br.mediaOverlayOpacity ?? 0.45) * 100)}%
                  </Typography>
                </Box>
              </StyleRow>
            </InspectorSection>
          </>
        )}

      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeroTextInspector — tagline / title / subtitle text + shared color
// ─────────────────────────────────────────────────────────────────────────────
export function HeroTextInspector({ settings, onSettingsChange, onClose }) {
  const up = (k, v) => onSettingsChange({ ...settings, [k]: v });
  const upB = (k, v) => onSettingsChange({ ...settings, branding: { ...(settings.branding || {}), [k]: v } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PanelHeader
        icon={<TextFieldsOutlined sx={{ fontSize: 14, color: FIELD_COLOR }} />}
        label="Hero Text"
        onClose={onClose}
        color={FIELD_COLOR}
      />
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <InspectorSection label="Content">
          <StyleRow label="Tagline">
            <StyleInput
              value={settings.headerTitle || ''}
              onChange={v => up('headerTitle', v)}
              placeholder="RESERVE YOUR SLOTS"
            />
          </StyleRow>
          <StyleRow label="Title">
            <StyleInput
              value={settings.headerSubtitle || ''}
              onChange={v => up('headerSubtitle', v)}
              placeholder="Event name…"
            />
          </StyleRow>
          <StyleRow label="Subtitle">
            <StyleInput
              value={settings.description || ''}
              onChange={v => up('description', v)}
              placeholder="Short description…"
            />
          </StyleRow>
        </InspectorSection>
        <Divider sx={{ borderColor: '#f0f2f5' }} />
        <InspectorSection label="Style">
          <StyleRow label="Text color">
            <ColorInput
              value={settings.heroTextColor || '#ffffff'}
              onChange={v => up('heroTextColor', v)}
            />
          </StyleRow>
          <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mt: 0.25 }}>
            Applies to tagline, title, and subtitle.
          </Typography>
        </InspectorSection>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormTemplatesPalette
// ─────────────────────────────────────────────────────────────────────────────
export function FormTemplatesPalette({ onApplyTemplate }) {
  const [pending, setPending] = React.useState(null);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid #e8edf2', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#7a8494', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.25 }}>Field Templates</Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8' }}>Click to apply to this form</Typography>
        </Box>
        <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto', flex: 1 }}>
          {FORM_TEMPLATES.map(tpl => (
            <Box
              key={tpl.id}
              onClick={() => setPending(tpl)}
              sx={{
                borderRadius: 2, border: '1px solid #e4e9f0', overflow: 'hidden',
                cursor: 'pointer', transition: 'all 0.15s',
                '&:hover': { borderColor: tpl.accent, boxShadow: `0 0 0 2px ${tpl.accent}22`, transform: 'translateY(-1px)' },
              }}
            >
              {/* Colour swatch header */}
              <Box sx={{ height: 56, background: tpl.bg, display: 'flex', alignItems: 'center', px: 1.5, gap: 1 }}>
                <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>{tpl.icon}</Typography>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>{tpl.name}</Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#7a8494', mt: 0.25 }}>{tpl.fields.length} fields</Typography>
                </Box>
              </Box>
              {/* Description */}
              <Box sx={{ px: 1.25, py: 1, background: '#fff' }}>
                <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3', lineHeight: 1.4 }}>{tpl.description}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Confirm modal ── */}
      {pending && (
        <Box
          sx={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(10,15,30,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
          onClick={() => setPending(null)}
        >
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ background: '#fff', borderRadius: 3, width: '100%', maxWidth: 420, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
          >
            {/* Warning band */}
            <Box sx={{ background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)', borderBottom: '1px solid #fde68a', px: 3, pt: 3, pb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: '#fef3c7', border: '1.5px solid #fcd34d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AutoAwesomeOutlined sx={{ fontSize: 24, color: '#d97706' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
                  Apply "{pending.name}"?
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5 }}>
                  This will replace all current fields in the active step with the template fields. This cannot be undone.
                </Typography>
              </Box>
            </Box>
            {/* Template preview */}
            <Box sx={{ px: 3, py: 1.5, background: '#fafbfc', borderBottom: '1px solid #e8edf2', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {pending.fields.slice(0, 6).map((f, i) => (
                <Box key={i} sx={{ px: 1, py: 0.25, borderRadius: 1, background: alpha(pending.accent, 0.08), border: `1px solid ${alpha(pending.accent, 0.18)}` }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: pending.accent }}>{f.label}</Typography>
                </Box>
              ))}
              {pending.fields.length > 6 && (
                <Box sx={{ px: 1, py: 0.25, borderRadius: 1, background: '#f0f2f5', border: '1px solid #e4e9f0' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#9ea8b3' }}>+{pending.fields.length - 6} more</Typography>
                </Box>
              )}
            </Box>
            {/* Actions */}
            <Box sx={{ px: 3, py: 2.5, display: 'flex', gap: 1.5 }}>
              <Box
                onClick={() => setPending(null)}
                sx={{ flex: 1, py: 1.1, borderRadius: 2, border: '1px solid #e4e9f0', background: '#f8f9fb', color: '#5a6474', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', '&:hover': { background: '#f0f2f5' } }}
              >
                Cancel
              </Box>
              <Box
                onClick={() => { onApplyTemplate(pending); setPending(null); }}
                sx={{ flex: 1.4, py: 1.1, borderRadius: 2, background: FIELD_COLOR, color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, '&:hover': { opacity: 0.88 } }}
              >
                <AutoAwesomeOutlined sx={{ fontSize: 15 }} />
                Apply Template
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Left panel tab definitions (exported so FormBuilderTab can use them)
// ─────────────────────────────────────────────────────────────────────────────

export function buildPanelTabs() {
  return [
    { key: 'fields',    label: 'Fields',    icon: <ViewQuiltOutlined  sx={{ fontSize: 14 }} /> },
    { key: 'templates', label: 'Templates', icon: <AutoAwesomeOutlined sx={{ fontSize: 14 }} /> },
  ];
}